import { useState, useEffect } from 'react'
import {
    deletePendingEvent,
    getPendingEvents,
} from '../../../indexedDb/pendingEvents'
import { sendAnalyticsEvent, sendDiscardMetric } from './sendGAAnalyticsEvent'

let isOfflineMode = false
let listeners = new Set()
let intervalId = null

export const setOfflineMode = value => {
    if (isOfflineMode !== value) {
        isOfflineMode = value
        listeners.forEach(cb => cb(isOfflineMode))

        if (!isOfflineMode) {
            setTimeout(() => {
                retryPendingEvents()
            }, 1000)
        }
    }
}

export const getOfflineMode = () => isOfflineMode

export const isFirefox = () => {
    return navigator.userAgent.toLowerCase().includes('firefox')
}

export const monitorOnlineOffline = (navigatorProp = navigator) => {
    const navigatorConnection = navigatorProp?.connection

    const checkConnectionSpeed = () => {
        const isCurrentlyOnline = navigatorProp.onLine

        if (isFirefox() || !navigatorConnection) {
            setOfflineMode(!isCurrentlyOnline)
            return
        }

        const connectionType = navigatorConnection?.effectiveType || 'unknown'
        const isSlowConnection =
            connectionType === 'slow-2g' || connectionType === '2g'

        setOfflineMode(isCurrentlyOnline ? isSlowConnection : true)
    }

    const handleOnline = () => checkConnectionSpeed()
    const handleOffline = () => setOfflineMode(true)

    if (navigatorConnection && !isFirefox()) {
        navigatorConnection.addEventListener('change', checkConnectionSpeed)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    checkConnectionSpeed()

    // 🔹 Start polling every 2 seconds in Firefox (since events are unreliable)
    if (isFirefox()) {
        intervalId = setInterval(checkConnectionSpeed, 2000)
    }

    return () => {
        if (navigatorConnection && !isFirefox()) {
            navigatorConnection.removeEventListener(
                'change',
                checkConnectionSpeed,
            )
        }
        window.removeEventListener('online', handleOnline)
        window.removeEventListener('offline', handleOffline)

        if (intervalId) {
            clearInterval(intervalId)
            intervalId = null
        }
    }
}

const retryPendingEvents = async () => {
    try {
        sendDiscardMetric()
        const pendingEvents = await getPendingEvents()

        if (pendingEvents.length > 0) {
            console.log(`Retrying ${pendingEvents.length} pending events`)

            for (const event of pendingEvents) {
                try {
                    sendAnalyticsEvent(event.event, event)
                    await deletePendingEvent(event.id)
                } catch (error) {
                    console.error(`Failed to send event: ${event.event}`, error)
                }
            }
        }
    } catch (error) {
        console.error('Error while retrying pending events:', error)
    }
}

export const useNetworkStatus = () => {
    const [isOffline, setIsOffline] = useState(getOfflineMode())

    useEffect(() => {
        let isMounted = true

        const stopMonitoring = monitorOnlineOffline()

        const updateOfflineStatus = newStatus => {
            if (isMounted) {
                setIsOffline(newStatus)
            }
        }

        listeners.add(updateOfflineStatus)

        return () => {
            isMounted = false
            stopMonitoring()
            listeners.delete(updateOfflineStatus)
        }
    }, [])

    return { isOffline }
}
